import React, { useEffect, useRef, useState } from 'react';
import { useNotify, useRedirect, useTranslate } from 'react-admin';
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Tab, Tabs, TextField, Typography } from '@mui/material';
import { apiClient, type ApiError } from '../../utils/apiClient';
import { ReleaseFileError, ReleaseUploadError, fileText, putPackage, readReleaseFiles, readUnsignedRelease, signRelease, type UnsignedSummary } from './releaseFiles';
import { errorLine, formatBytes } from './labels';
import { deploymentWarning, keyErrorLine } from '../kiosk-release-keys/KeyFileDialogs';
import { ReleaseKeyError, openReleaseKey, type SigningKey } from '../kiosk-release-keys/releaseKeyCrypto';
import { saveBlob } from '../kiosk-release-keys/saveBlob';
import type { ReleaseKey } from '../kiosk-release-keys/types';
import { ManifestRuleError } from './manifestRules';
import type { Release, UploadLink } from './types';

const BASE = '/api/backend/kiosk-releases';
const KEYS = '/api/backend/kiosk-release-keys';

type Mode = 'sign' | 'signed';

interface Checked {
  manifestBytes: Uint8Array;
  summary: UnsignedSummary;
  key: SigningKey;
  manifestName: string;
  packageName: string;
}

interface SignedBody {
  body: { manifestB64: string; signatureB64: string; notes?: string };
  files: SignedFiles | null;
}

interface SignedFiles {
  signedManifest: Uint8Array;
  signatureB64: string;
}

interface Stuck {
  release: Release;
  stored: boolean;
}

const GONE_CODES = ['KIOSK_RELEASE_NOT_FOUND', 'KIOSK_RELEASE_NOT_READY'];
const ALREADY_STORED_STATUSES = [400, 409];

const samePackage = (a: File, b: File) => a.name === b.name && a.size === b.size && a.lastModified === b.lastModified;

interface Props {
  open: boolean;
  onClose: () => void;
}

const FilePick = ({ label, accept, testId, disabled = false, onPick }: { label: string; accept: string; testId: string; disabled?: boolean; onPick: (file: File | null) => void }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <input data-testid={testId} type="file" accept={accept} disabled={disabled} onChange={(event) => onPick(event.target.files && event.target.files.length > 0 ? event.target.files[0] : null)} />
  </Box>
);

export const UploadReleaseDialog: React.FC<Props> = ({ open, onClose }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const redirect = useRedirect();
  const [packageFile, setPackageFile] = useState<File | null>(null);
  const [manifestFile, setManifestFile] = useState<File | null>(null);
  const [signatureFile, setSignatureFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [stuck, setStuck] = useState<Stuck | null>(null);
  const [session, setSession] = useState(0);
  const [mode, setMode] = useState<Mode>('sign');
  const [unsignedFile, setUnsignedFile] = useState<File | null>(null);
  const [keyFile, setKeyFile] = useState<File | null>(null);
  const [password, setPassword] = useState('');
  const [notes, setNotes] = useState('');
  const [checked, setChecked] = useState<Checked | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [signedFiles, setSignedFiles] = useState<SignedFiles | null>(null);
  const [doneRelease, setDoneRelease] = useState<Release | null>(null);
  const [benchRefused, setBenchRefused] = useState<File | null>(null);
  const checkGeneration = useRef(0);
  const t = (key: string) => translate(`resources.kiosk-releases.upload.${key}`);

  useEffect(() => {
    if (open) return;
    setPackageFile(null);
    setManifestFile(null);
    setSignatureFile(null);
    setProblem(null);
    setStuck(null);
    setBusy(false);
    setMode('sign');
    setUnsignedFile(null);
    setKeyFile(null);
    setPassword('');
    setNotes('');
    setChecked(null);
    setWarning(null);
    setSignedFiles(null);
    setDoneRelease(null);
    setBenchRefused(null);
    checkGeneration.current += 1;
    setSession((value) => value + 1);
  }, [open]);

  const fileProblem = (error: unknown): string => {
    if (error instanceof ReleaseFileError) {
      return error.reason === 'package_mismatch' ? translate('resources.kiosk-release-keys.errors.package_mismatch') : t(`fileErrors.${error.reason}`);
    }
    if (error instanceof ManifestRuleError) return translate('resources.kiosk-releases.upload.manifestRefused', { reason: error.reason });
    return keyErrorLine(error, translate);
  };

  const refusal = (error: ApiError): string => {
    if (error.errorCode === 'KIOSK_RELEASE_BENCH_BUILD' && typeof error.details?.entry === 'string' && typeof error.details?.marker === 'string') {
      return translate('resources.kiosk-releases.upload.benchBuild', { entry: error.details.entry, marker: error.details.marker });
    }
    if (error.errorCode !== undefined && GONE_CODES.includes(error.errorCode)) return t('releaseGone');
    if (error.errorCode === 'KIOSK_RELEASE_PACKAGE_MISMATCH' && typeof error.details?.reason === 'string') {
      return error.details.reason === 'missing' ? t('mismatchMissing') : translate('resources.kiosk-releases.upload.mismatchReason', { reason: error.details.reason });
    }
    return errorLine(error, translate);
  };

  const complete = async (release: Release, putStatus: number | null = null) => {
    try {
      const done = await apiClient.post<{ data: Release }>(`${BASE}/${release.id}/complete`);
      notify(t('done'), { type: 'success' });
      if (mode === 'sign') {
        setDoneRelease(done.data);
        return;
      }
      onClose();
      redirect('show', 'kiosk-releases', done.data.id);
    } catch (error) {
      const failure = error as ApiError;
      const gone = failure.errorCode !== undefined && GONE_CODES.includes(failure.errorCode);
      const bench = failure.errorCode === 'KIOSK_RELEASE_BENCH_BUILD';
      if (bench) setBenchRefused(packageFile);
      setStuck(gone || bench ? null : { release, stored: failure.errorCode !== 'KIOSK_RELEASE_PACKAGE_MISMATCH' });
      const missing = failure.errorCode === 'KIOSK_RELEASE_PACKAGE_MISMATCH' && failure.details?.reason === 'missing';
      setProblem(missing && putStatus !== null ? translate('resources.kiosk-releases.upload.fileErrors.upload_failed_status', { status: putStatus }) : refusal(failure));
    }
  };

  const finish = async (release: Release, upload: UploadLink, file: File) => {
    try {
      await putPackage(upload.uploadUrl, file);
    } catch (error) {
      if (error instanceof ReleaseUploadError && ALREADY_STORED_STATUSES.includes(error.status)) {
        await complete(release, error.status);
        return;
      }
      setStuck({ release, stored: false });
      setProblem(t('fileErrors.upload_failed'));
      return;
    }
    await complete(release);
  };

  const check = async () => {
    if (packageFile === null || unsignedFile === null || keyFile === null) return;
    const typed = password;
    const generation = checkGeneration.current + 1;
    checkGeneration.current = generation;
    const stale = () => checkGeneration.current !== generation;
    const manifestName = unsignedFile.name;
    const packageName = packageFile.name;
    setPassword('');
    setBusy(true);
    setProblem(null);
    setChecked(null);
    setWarning(null);
    setSignedFiles(null);
    try {
      const read = await readUnsignedRelease(unsignedFile, packageFile);
      if (stale()) return;
      const text = await fileText(keyFile);
      if (stale()) return;
      const key = await openReleaseKey(text, typed);
      if (stale()) return;
      const keys = await apiClient.get<{ data: ReleaseKey[] }>(KEYS);
      if (stale()) return;
      const active = keys.data.find((candidate) => candidate.status === 'active');
      if (active === undefined) {
        setProblem(translate('resources.kiosk-releases.errors.KIOSK_RELEASE_KEY_NOT_SET'));
        return;
      }
      if (key.keyId !== active.keyId) {
        setProblem(translate('resources.kiosk-release-keys.errors.not_active_key'));
        return;
      }
      setWarning(deploymentWarning(String(JSON.parse(text).deployment), translate));
      setChecked({ manifestBytes: read.manifestBytes, summary: read.summary, key, manifestName, packageName });
    } catch (error) {
      if (!stale()) setProblem(fileProblem(error));
    } finally {
      setBusy(false);
    }
  };

  const signedBody = async (): Promise<SignedBody | null> => {
    if (checked === null) return null;
    const { manifestBytes, key } = checked;
    setChecked(null);
    const signed = await signRelease(manifestBytes, key);
    const files = { signedManifest: signed.signedManifest, signatureB64: signed.signatureB64 };
    const trimmed = notes.trim();
    return { body: trimmed === '' ? { manifestB64: signed.manifestB64, signatureB64: signed.signatureB64 } : { manifestB64: signed.manifestB64, signatureB64: signed.signatureB64, notes: trimmed }, files };
  };

  const saveSigned = () => {
    if (signedFiles === null) return;
    saveBlob(new Blob([new Uint8Array(signedFiles.signedManifest)], { type: 'application/json' }), 'manifest.json');
    saveBlob(new Blob([signedFiles.signatureB64], { type: 'text/plain' }), 'manifest.sig');
  };

  const openRelease = () => {
    if (doneRelease === null) return;
    onClose();
    redirect('show', 'kiosk-releases', doneRelease.id);
  };

  const submit = async () => {
    if (packageFile === null) return;
    if (mode === 'signed' && (manifestFile === null || signatureFile === null)) return;
    if (mode === 'sign' && stuck === null && checked === null) return;
    setBusy(true);
    setProblem(null);
    try {
      if (stuck !== null) {
        if (stuck.stored) {
          await complete(stuck.release);
          return;
        }
        let link: { data: UploadLink };
        try {
          link = await apiClient.post<{ data: UploadLink }>(`${BASE}/${stuck.release.id}/upload-url`);
        } catch (error) {
          const failure = error as ApiError;
          if (failure.errorCode !== undefined && GONE_CODES.includes(failure.errorCode)) setStuck(null);
          setProblem(refusal(failure));
          return;
        }
        await finish(stuck.release, link.data, packageFile);
        return;
      }
      let prepared: SignedBody | null;
      try {
        prepared = mode === 'sign' ? await signedBody() : { body: await readReleaseFiles(manifestFile as File, signatureFile as File, packageFile), files: null };
      } catch (error) {
        if (error instanceof ReleaseFileError || error instanceof ManifestRuleError || error instanceof ReleaseKeyError) {
          setProblem(fileProblem(error));
          return;
        }
        throw error;
      }
      if (prepared === null) return;
      const registered = await apiClient.post<{ data: Release; upload: UploadLink }>(BASE, prepared.body);
      setSignedFiles(prepared.files);
      await finish(registered.data, registered.upload, packageFile);
    } catch (error) {
      setProblem(refusal(error as ApiError));
    } finally {
      setBusy(false);
    }
  };

  const pickReleaseFile = (set: (file: File | null) => void) => (file: File | null) => {
    set(file);
    if (file !== null) setStuck(null);
  };

  const forgetCheck = () => {
    checkGeneration.current += 1;
    setChecked(null);
    setWarning(null);
    setSignedFiles(null);
  };

  const pickSignFile = (set: (file: File | null) => void) => (file: File | null) => {
    set(file);
    forgetCheck();
    if (file !== null) setStuck(null);
  };

  const pickPackage = (file: File | null) => {
    setPackageFile(file);
    if (file !== null && benchRefused !== null && !samePackage(file, benchRefused)) setBenchRefused(null);
    forgetCheck();
  };

  const switchMode = (next: Mode) => {
    setMode(next);
    setProblem(null);
    forgetCheck();
  };

  const ready = benchRefused === null && (mode === 'sign'
    ? packageFile !== null && (checked !== null || stuck !== null) && !busy
    : packageFile !== null && manifestFile !== null && signatureFile !== null && !busy);
  const canCheck = packageFile !== null && unsignedFile !== null && keyFile !== null && password !== '' && !busy;
  const summaryRows = ({ summary, manifestName, packageName }: Checked): [string, string][] => [
    ['manifestFile', manifestName],
    ['packageFile', packageName],
    ['kind', translate(`resources.kiosk-releases.kinds.${summary.kind}`)],
    ['version', summary.version],
    ['build', String(summary.build)],
    ['commit', summary.gitCommit],
    ['environments', summary.environmentPolicy.join(', ')],
    ['octopusAffecting', translate(summary.octopusAffecting ? 'ra.boolean.true' : 'ra.boolean.false')],
    ['journal', `${summary.journalSchema === null ? '-' : String(summary.journalSchema)}${summary.journalBreaking ? ` (${translate('resources.kiosk-releases.badges.journalChange')})` : ''}`],
    ['files', String(summary.fileCount)],
    ['package', `${formatBytes(summary.packageSize)}, SHA-256 ${summary.packageSha256}`],
  ];
  const submitLabel = stuck !== null ? t('again') : mode === 'sign' ? t('signAndUpload') : t('submit');

  return (
    <Dialog open={open} onClose={() => !busy && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>{t('title')}</DialogTitle>
      <DialogContent>
        {doneRelease !== null ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="success">{t('done')}</Alert>
            {signedFiles !== null && <Box><Button variant="outlined" onClick={saveSigned} sx={{ textTransform: 'none' }}>{t('saveSigned')}</Button></Box>}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Tabs value={mode} onChange={(_, next: Mode) => switchMode(next)} variant="fullWidth">
              <Tab value="sign" label={t('tabs.sign')} disabled={busy || stuck !== null} sx={{ textTransform: 'none' }} />
              <Tab value="signed" label={t('tabs.signed')} disabled={busy || stuck !== null} sx={{ textTransform: 'none' }} />
            </Tabs>
            <FilePick key={`package-${session}`} label={t('package')} accept=".zip,application/zip" testId="release-package" disabled={busy} onPick={pickPackage} />
            {mode === 'sign' ? (
              <>
                <FilePick key={`unsigned-${session}`} label={t('unsignedManifest')} accept=".json,application/json" testId="release-unsigned-manifest" disabled={busy} onPick={pickSignFile(setUnsignedFile)} />
                <FilePick key={`key-${session}`} label={t('keyFile')} accept=".json,application/json" testId="release-key-file" disabled={busy} onPick={pickSignFile(setKeyFile)} />
                <TextField type="password" label={t('password')} value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="current-password" disabled={busy} inputProps={{ 'data-testid': 'release-key-password' }} />
                <TextField label={translate('resources.kiosk-releases.fields.notes')} value={notes} onChange={(event) => setNotes(event.target.value)} disabled={busy} multiline inputProps={{ 'data-testid': 'release-notes', maxLength: 2000 }} />
                <Box>
                  <Button variant="outlined" disabled={!canCheck} onClick={check} sx={{ textTransform: 'none' }}>{t('review')}</Button>
                </Box>
                {warning !== null && <Alert severity="warning">{warning}</Alert>}
                {checked !== null && (
                  <Box data-testid="release-summary" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {summaryRows(checked).map(([label, value]) => (
                      <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                        <Typography variant="body2" color="text.secondary">{t(`reviewLabels.${label}`)}</Typography>
                        <Typography variant="body2" sx={{ textAlign: 'right', wordBreak: 'break-all' }}>{value}</Typography>
                      </Box>
                    ))}
                  </Box>
                )}
                {signedFiles !== null && <Box><Button variant="text" onClick={saveSigned} sx={{ textTransform: 'none' }}>{t('saveSigned')}</Button></Box>}
              </>
            ) : (
              <>
                <Typography variant="body2">{t('intro')}</Typography>
                <Typography variant="body2" color="text.secondary">{t('signatureNote')}</Typography>
                <FilePick key={`manifest-${session}`} label={t('manifest')} accept=".json,application/json" testId="release-manifest" disabled={busy} onPick={pickReleaseFile(setManifestFile)} />
                <FilePick key={`signature-${session}`} label={t('signature')} accept=".sig" testId="release-signature" disabled={busy} onPick={pickReleaseFile(setSignatureFile)} />
              </>
            )}
            {problem !== null && <Alert severity="error">{problem}</Alert>}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {doneRelease !== null ? (
          <Button variant="contained" onClick={openRelease} sx={{ textTransform: 'none' }}>{t('openRelease')}</Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={busy} sx={{ textTransform: 'none' }}>{translate('ra.action.cancel')}</Button>
            <Button onClick={submit} variant="contained" disabled={!ready} startIcon={busy ? <CircularProgress size={16} /> : undefined} sx={{ textTransform: 'none' }}>
              {submitLabel}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
